<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import EvolutionConnectionsAPI from 'dashboard/api/evolutionConnections';

const COPY = {
  pt: {
    connected: 'Conectado',
    connecting: 'Conectando',
    disconnected: 'Desconectado',
    reconnecting: 'Reconectando',
    blocked: 'Bloqueado pelo WhatsApp',
    conflict: 'Conflito de instância',
    unknown: 'Estado desconhecido',
    qr: 'Gerar QR',
    restart: 'Reiniciar',
    restartTitle: 'Reiniciar a conexão?',
    restartHelp: 'A linha cai por alguns segundos e tenta voltar.',
    cancel: 'Cancelar',
    close: 'Fechar',
    qrHelp: 'Leia o QR no celular dessa linha.',
    pairing: 'Código de pareamento',
    qrRefresh: 'O QR troca sozinho até a linha conectar.',
    qrMissing: 'A Evolution não devolveu um QR. Tente de novo.',
    lineConnected: 'Linha conectada.',
    restartSent: 'Reinício pedido.',
    failed: 'Não foi possível falar com a Evolution.',
    warningOne: name => `Não foi possível consultar a conexão de ${name}.`,
    warningMany: names =>
      `Não foi possível consultar a conexão de ${names.slice(0, -1).join(', ')} e ${names.at(-1)}.`,
  },
  en: {
    connected: 'Connected',
    connecting: 'Connecting',
    disconnected: 'Disconnected',
    reconnecting: 'Reconnecting',
    blocked: 'Blocked by WhatsApp',
    conflict: 'Instance conflict',
    unknown: 'Unknown state',
    qr: 'Generate QR',
    restart: 'Restart',
    restartTitle: 'Restart the connection?',
    restartHelp: 'The line drops for a few seconds and tries to come back.',
    cancel: 'Cancel',
    close: 'Close',
    qrHelp: 'Scan the QR code on this line’s phone.',
    pairing: 'Pairing code',
    qrRefresh: 'The QR code refreshes on its own until the line connects.',
    qrMissing: 'Evolution did not return a QR code. Try again.',
    lineConnected: 'Line connected.',
    restartSent: 'Restart requested.',
    failed: 'Could not reach Evolution.',
    warningOne: name => `Could not check the connection for ${name}.`,
    warningMany: names =>
      `Could not check the connection for ${names.slice(0, -1).join(', ')} and ${names.at(-1)}.`,
  },
};

const getters = useStoreGetters();
const store = useStore();
const { t, locale } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');
const connection = ref({ enabled: false, warnings: [], inboxes: {} });
const busyId = ref(null);
const qrDialog = ref(null);
const restartDialog = ref(null);
const qrInbox = ref(null);
const qrImage = ref('');
const pairingCode = ref('');
const restartInbox = ref(null);
const restarting = ref(false);

let pollTimer = null;
let qrTimer = null;
let statusFlight = null;
let toldFailure = false;
let announcedConnection = false;

const copy = computed(() =>
  String(locale.value).toLowerCase().startsWith('pt') ? COPY.pt : COPY.en
);

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const warningText = computed(() => {
  const names = connection.value.warnings || [];
  if (!names.length) return '';
  if (names.length === 1) return copy.value.warningOne(names[0]);
  return copy.value.warningMany(names);
});

const restartDescription = computed(() => {
  const name = restartInbox.value?.name;
  if (!name) return copy.value.restartHelp;
  return `${name}. ${copy.value.restartHelp}`;
});

const rowState = inbox => {
  if (!isAdmin.value || inbox.channel_type !== 'Channel::Api') return null;
  return connection.value.inboxes?.[String(inbox.id)] || null;
};

const stateLabel = state => copy.value[state] || copy.value.unknown;

const stateClass = state => {
  if (state === 'connected') return 'text-n-teal-11';
  if (state === 'connecting' || state === 'reconnecting') return 'text-n-amber-11';
  if (state === 'disconnected') return 'text-n-ruby-11';
  return 'text-n-slate-11';
};

const actionFor = state => {
  if (state === 'disconnected') return 'qr';
  if (state === 'connecting' || state === 'reconnecting') return 'restart';
  return null;
};

const errorText = error => error?.response?.data?.error || copy.value.failed;

const loadStatus = async (fresh = false) => {
  if (!isAdmin.value || statusFlight) return;
  statusFlight = EvolutionConnectionsAPI.getStatus(fresh)
    .then(({ data }) => {
      connection.value = {
        enabled: Boolean(data.enabled),
        warnings: data.warnings || [],
        inboxes: data.inboxes || {},
      };
      toldFailure = false;
      noticeIfConnected();
    })
    .catch(error => {
      if (!toldFailure) {
        useAlert(errorText(error));
        toldFailure = true;
      }
    })
    .finally(() => {
      statusFlight = null;
    });
};

const noticeIfConnected = () => {
  if (!qrInbox.value) return;
  const row = connection.value.inboxes[String(qrInbox.value.id)];
  if (row?.state === 'connected') finishConnected();
};

const finishConnected = () => {
  if (announcedConnection) return;
  announcedConnection = true;
  closeQr();
  useAlert(copy.value.lineConnected);
};

const startPoll = () => {
  stopPoll();
  if (!isAdmin.value) return;
  loadStatus(false);
  pollTimer = setInterval(() => {
    if (document.hidden) return;
    loadStatus(false);
  }, 15000);
};

const stopPoll = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
};

const onVisibility = () => {
  if (!document.hidden && isAdmin.value) loadStatus(false);
};

const safeImage = value =>
  typeof value === 'string' && /^data:image\/(png|jpeg|webp);/i.test(value)
    ? value
    : '';

const applyQr = data => {
  if (data.connected) {
    finishConnected();
    loadStatus(true);
    return false;
  }
  const image = safeImage(data.image);
  if (!image) {
    useAlert(copy.value.qrMissing);
    return false;
  }
  qrImage.value = image;
  pairingCode.value = data.pairing_code || '';
  return true;
};

const openQr = async inbox => {
  if (busyId.value) return;
  busyId.value = inbox.id;
  try {
    const { data } = await EvolutionConnectionsAPI.qrcode(inbox.id);
    if (data.connected) {
      useAlert(copy.value.lineConnected);
      loadStatus(true);
      return;
    }
    const image = safeImage(data.image);
    if (!image) {
      useAlert(copy.value.qrMissing);
      return;
    }
    announcedConnection = false;
    qrInbox.value = inbox;
    qrImage.value = image;
    pairingCode.value = data.pairing_code || '';
    qrDialog.value?.open();
    startQrTimer();
  } catch (error) {
    useAlert(errorText(error));
  } finally {
    busyId.value = null;
  }
};

const refreshQr = async () => {
  if (!qrInbox.value || busyId.value) return;
  try {
    const { data } = await EvolutionConnectionsAPI.qrcode(qrInbox.value.id);
    applyQr(data);
  } catch (error) {
    useAlert(errorText(error));
  }
};

const startQrTimer = () => {
  stopQrTimer();
  qrTimer = setInterval(() => {
    if (document.hidden) return;
    refreshQr();
  }, 20000);
};

const stopQrTimer = () => {
  if (qrTimer) clearInterval(qrTimer);
  qrTimer = null;
};

const resetQr = () => {
  stopQrTimer();
  qrInbox.value = null;
  qrImage.value = '';
  pairingCode.value = '';
};

const closeQr = () => {
  resetQr();
  qrDialog.value?.close();
};

const askRestart = inbox => {
  restartInbox.value = inbox;
  restartDialog.value?.open();
};

const confirmRestart = async () => {
  const inbox = restartInbox.value;
  if (!inbox || restarting.value) return;
  restarting.value = true;
  try {
    await EvolutionConnectionsAPI.restart(inbox.id);
    restartDialog.value?.close();
    useAlert(copy.value.restartSent);
    loadStatus(true);
  } catch (error) {
    useAlert(errorText(error));
  } finally {
    restarting.value = false;
  }
};

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};

watch(isAdmin, () => startPoll(), { immediate: true });

document.addEventListener('visibilitychange', onVisibility);
onBeforeUnmount(() => {
  stopPoll();
  stopQrTimer();
  document.removeEventListener('visibilitychange', onVisibility);
});
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        feature-name="inboxes"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <p
        v-if="warningText"
        class="mb-0 py-3 text-sm text-n-amber-11"
        role="status"
      >
        {{ warningText }}
      </p>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="flex flex-wrap items-start justify-between gap-4 py-4"
        >
          <div class="flex min-w-0 items-center gap-4">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
            </div>
            <div class="flex min-w-0 flex-col items-start gap-1">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ inbox.name }}
              </span>
              <ChannelName
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
                :voice-enabled="inbox.voice_enabled"
                class="text-body-main text-n-slate-11"
              />
              <span
                v-if="rowState(inbox)"
                class="inline-flex flex-wrap items-center gap-x-2 text-sm"
              >
                <span
                  class="inline-flex items-center gap-1.5"
                  :class="stateClass(rowState(inbox).state)"
                >
                  <span class="size-1.5 rounded-full bg-current" aria-hidden="true" />
                  {{ stateLabel(rowState(inbox).state) }}
                </span>
                <span v-if="rowState(inbox).phone" class="text-n-slate-11">
                  {{ rowState(inbox).phone }}
                </span>
              </span>
            </div>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-3">
            <Button
              v-if="actionFor(rowState(inbox)?.state) === 'qr'"
              size="sm"
              type="button"
              :label="copy.qr"
              :is-loading="busyId === inbox.id"
              :disabled="Boolean(busyId)"
              @click="openQr(inbox)"
            />
            <Button
              v-if="actionFor(rowState(inbox)?.state) === 'restart'"
              variant="ghost"
              color="slate"
              size="sm"
              type="button"
              :label="copy.restart"
              :disabled="Boolean(busyId) || restarting"
              @click="askRestart(inbox)"
            />
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(inbox)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />

    <Dialog
      ref="qrDialog"
      :title="qrInbox?.name || ''"
      :description="copy.qrHelp"
      :show-confirm-button="false"
      :cancel-button-label="copy.close"
      width="md"
      @close="resetQr"
    >
      <div class="flex flex-col items-center gap-4">
        <div class="bg-white p-3">
          <img
            v-if="qrImage"
            :src="qrImage"
            alt="QR Code para conectar o WhatsApp"
            width="220"
            height="220"
            class="block size-[220px]"
          />
        </div>
        <p v-if="pairingCode" class="mb-0 text-center">
          <span class="block text-sm text-n-slate-11">{{ copy.pairing }}</span>
          <span class="font-mono text-lg text-n-slate-12">{{ pairingCode }}</span>
        </p>
        <p class="mb-0 text-center text-sm text-n-slate-11">
          {{ copy.qrRefresh }}
        </p>
      </div>
    </Dialog>

    <Dialog
      ref="restartDialog"
      type="edit"
      :title="copy.restartTitle"
      :description="restartDescription"
      :confirm-button-label="copy.restart"
      :cancel-button-label="copy.cancel"
      :is-loading="restarting"
      :disable-confirm-button="restarting"
      @confirm="confirmRestart"
    />
  </SettingsLayout>
</template>
